import { isNil } from 'lodash-es'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { getUpliftsSum } from '@/features/upsell/upsellRemoteRepository'

const useUpsellInnerStore = defineStore('upsellInner', () => {
	const upliftsSum = ref<number | undefined>()
	const isLoading = ref(false)

	return {
		upliftsSum,
		isLoading
	}
})

export const useUpsellStore = defineStore('upsell', () => {
	const innerStore = useUpsellInnerStore()
	const { trackEvent } = useTelemetry()
	const upliftsSum = computed(() => innerStore.upliftsSum)
	const isLoading = computed(() => innerStore.isLoading)
	const isInitialUpliftSumLoaded = ref(false)
	const { favouriteSKUIds } = storeToRefs(useSkusStore())

	const shouldShowUpsellModal = ref(false)
	const upsellModalType = ref<'platforms' | 'automation' | 'revenue'>('platforms')

	async function fetch() {
		try {
			innerStore.isLoading = true

			const newValue = await getUpliftsSum()

			innerStore.upliftsSum = isNil(newValue) || newValue < 100 ? undefined : newValue
		} finally {
			innerStore.isLoading = false
			isInitialUpliftSumLoaded.value = true
		}
	}

	function openPlatformsUpsellModal() {
		trackEvent('user', 'Opened platforms upsell modal.')
		upsellModalType.value = 'platforms'
		shouldShowUpsellModal.value = true
	}

	function openAutomationUpsellModal() {
		trackEvent('user', 'Opened automation upsell modal.')

		upsellModalType.value = 'automation'
		shouldShowUpsellModal.value = true
	}

	function openRevenueUpsellModal() {
		trackEvent('user', 'Opened revenue upsell modal.')

		upsellModalType.value = 'revenue'
		shouldShowUpsellModal.value = true
	}

	function refreshUpliftSum() {
		if (!isInitialUpliftSumLoaded.value || isLoading.value) {
			return
		}

		trackEvent('app', 'Refreshing uplift sum.')

		fetch()
	}

	watch(favouriteSKUIds, refreshUpliftSum)

	return {
		fetch,
		upliftsSum,
		isLoading,
		shouldShowUpsellModal,
		upsellModalType,
		openPlatformsUpsellModal,
		openAutomationUpsellModal,
		openRevenueUpsellModal
	}
})
